<template>
  <div class="tooltip" @click="toggleTooltip">
    <slot></slot>
    <div
      v-if="isOpen"
      :class="['tooltip-content-wrapper', customClass, { show: isOpen }]"
      ref="tooltipElementRef"
      @click.stop
      aria-modal="true"
      role="dialog"
      :aria-hidden="!isOpen"
    >
      <slot name="tooltipContent"></slot>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, watch, toRefs, defineEmits } from 'vue';

  const props = defineProps({
    text: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: 'bottom',
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  });

  const { modelValue } = toRefs(props);

  const isOpen = ref(false);
  const tooltipElementRef = ref(null);
  const tooltipId = ref(`tooltip-${Math.random().toString(36).substr(2, 9)}`);

  const emit = defineEmits(['update:modelValue']);

  // Global event bus for tooltip management
  const createGlobalEventBus = () => {
    if (!window.tooltipEventBus) {
      window.tooltipEventBus = {
        listeners: new Map(),
        emit(event, data) {
          const callbacks = this.listeners.get(event) || [];
          callbacks.forEach(callback => callback(data));
        },
        on(event, callback) {
          if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
          }
          this.listeners.get(event).push(callback);
        },
        off(event, callback) {
          const callbacks = this.listeners.get(event) || [];
          const index = callbacks.indexOf(callback);
          if (index > -1) {
            callbacks.splice(index, 1);
          }
        }
      };
    }
    return window.tooltipEventBus;
  };

  const eventBus = createGlobalEventBus();

  const closeTooltip = () => {
    isOpen.value = false;
    emit('update:modelValue', false);
  };

  const openTooltip = () => {
    // Emit event to close all other tooltips
    eventBus.emit('closeAllTooltips', tooltipId.value);
    
    isOpen.value = true;
    emit('update:modelValue', true);
  };

  const toggleTooltip = () => {
    if (isOpen.value) {
      closeTooltip();
    } else {
      openTooltip();
    }
  };

  const handleCloseAllTooltips = (excludeId) => {
    if (excludeId !== tooltipId.value && isOpen.value) {
      closeTooltip();
    }
  };

  const handleClickOutside = (event) => {
    if (isOpen.value && !tooltipElementRef.value?.contains(event.target) && !event.target.closest('.tooltip')) {
      closeTooltip();
    }
  };

  const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && isOpen.value) {
      closeTooltip();
    }
  };

  onMounted(() => {
    // Register event listeners
    eventBus.on('closeAllTooltips', handleCloseAllTooltips);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
  });

  onUnmounted(() => {
    // Clean up event listeners
    eventBus.off('closeAllTooltips', handleCloseAllTooltips);
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleEscapeKey);
  });

  watch(modelValue, (newVal) => {
    if (newVal && !isOpen.value) {
      openTooltip();
    } else if (!newVal && isOpen.value) {
      closeTooltip();
    }
  });
</script>
